import type { GetReadyCustomTaskInstance } from "../../types/task";
import type { JourneyTaskOccurrence } from "./journeyTaskOccurrenceAdapter";

/**
 * Formats a custom task's raw ISO `deadline` into a human-readable display
 * string. Journey tasks already carry pre-formatted human deadlines (e.g.
 * "On Cycle Day 1"); only custom tasks store a raw ISO timestamp, so this is
 * the single point that humanises them for every Track surface.
 *
 * Custom tasks are always date-only (the create form has no time picker;
 * deadlines are stored at UTC midnight), so this ALWAYS formats weekday + short
 * date + year with NO time-of-day, e.g. "Wednesday, Jun 24, 2026". Formatting
 * is done in UTC so the displayed day matches the picked date regardless of the
 * viewer's timezone.
 * - Missing / unparseable → returned as-is (never crashes).
 */
function formatCustomDeadline(deadline: string, locale: string): string {
	const d = new Date(deadline);
	if (Number.isNaN(d.getTime())) return deadline;

	// Custom tasks are date-only (the create form has no time picker; deadlines
	// are stored at UTC midnight). Always show weekday + date with NO time, and
	// format in UTC so the displayed day matches the picked date regardless of
	// the viewer's timezone (a local-tz format would shift e.g. UTC midnight to
	// the previous/next day).
	return new Intl.DateTimeFormat(locale, {
		weekday: "long",
		month: "short",
		day: "numeric",
		year: "numeric",
		timeZone: "UTC",
	}).format(d);
}

/**
 * Maps user-created custom tasks with a `deadline` into the same
 * `JourneyTaskOccurrence` shape consumed by TwoWeekCalendar and ComingUpFeed.
 *
 * Filtering rules:
 * - Tasks with no deadline are excluded (nothing to place on the calendar).
 * - Tasks whose deadline isn't a parseable date are excluded: Track buckets by
 *   `startIso.slice(0, 10)`, so a non-ISO value would never land in any
 *   calendar/feed cell and would silently vanish — skip it rather than emit a
 *   ghost occurrence.
 * - Completed tasks are excluded (already done, no need to surface on Track).
 *
 * The `startIso`/`endIso` are both set to the task's raw `deadline` value so the
 * occurrence lands in exactly one calendar day cell (the calendar positions by
 * these — they MUST stay raw ISO). The `task` field is synthesised from the
 * available custom-task data using a minimal JourneyTask shape — enough for
 * `JourneyTaskDetailSheet` to render the title and close; rich fields
 * (description, iconName, etc.) are left as defaults. The synthesised
 * `task.deadline` is the human-readable DISPLAY string (formatted via `locale`),
 * matching how journey occurrences already carry human deadline text.
 */
export function selectCustomTaskOccurrences(
	tasks: GetReadyCustomTaskInstance[],
	locale: string,
): JourneyTaskOccurrence[] {
	const occurrences: JourneyTaskOccurrence[] = [];

	for (const task of tasks) {
		if (!task.deadline) continue;
		if (task.status === "completed") continue;
		// Skip non-ISO/unparseable deadlines: Track buckets by startIso.slice(0,10),
		// so these would never appear in any calendar/feed cell.
		if (Number.isNaN(new Date(task.deadline).getTime())) continue;

		occurrences.push({
			id: `custom:${task.id}`,
			taskId: task.id,
			stepId: "custom",
			startIso: task.deadline,
			endIso: task.deadline,
			title: task.title,
			status: "in_progress",
			source: "journey",
			// Synthesised JourneyTask — enough for the detail sheet title row.
			task: {
				id: task.id,
				title: task.title,
				description: task.description ?? "",
				// Custom tasks have no clinical category; "clinical" is the generic
				// default (TASK_CATEGORIES = clinical|medication|cycle). taskDomain
				// stays undefined so no colored domain dot is shown.
				category: "clinical",
				deadline: formatCustomDeadline(task.deadline, locale),
				status: "ongoing",
				source: "patient",
			},
			taskDomain: undefined,
		});
	}

	return occurrences;
}
